import { assetDisplayName, originalFileLine } from './displayName.js';
import { assertAssetPath, ORIGINALS_PREFIX, RENDITIONS_PREFIX } from './paths.js';

// PURGE NOW (2026-10-09) — the takedown mechanism, PURE. See
// docs/portal-assets-takedown.md for the human procedure and the honest promise.
//
// ⚠️ A purge is NOT a delete. A delete is soft: the asset leaves the portal, the
// FILE stays, and Kyck can restore it for RETENTION_DAYS (deletion.ts). A purge
// removes the original and every rendition from the bucket, invalidates the CDN
// cache for the renditions, stamps `purgedAt` and writes an audit entry carrying
// a REASON. It cannot be undone by us. It works on a live asset as well as a
// deleted one: a consent withdrawal must not have to wait for anyone to delete
// first, and must not wait for any window.
//
// ⚠️⚠️ WHERE THIS RUNS IS THE POINT. Deleting objects is the capability the
// asset worker and the video Job were deliberately stripped of (they decode
// attacker-controlled media). Purge lives in the APP, which already holds the
// credentials and decodes nothing. purgeStaysInApp.test.ts keeps it that way.

export const STAFF_PURGE_REASONS = ['consent_withdrawal', 'legal_request', 'uploaded_in_error', 'other'] as const;
/**
 * ⚠️ `retention_expired` is the SWEEP's reason (2026-10-09) and is deliberately NOT
 * in STAFF_PURGE_REASONS: the staff schema and the Purge-now dropdown read that
 * list, because a person choosing it would write "the retention window elapsed"
 * into the audit trail against an asset that may be live.
 */
export const PURGE_REASONS = [...STAFF_PURGE_REASONS, 'retention_expired'] as const;
export type PurgeReason = (typeof PURGE_REASONS)[number];

export const PURGE_REASON_LABELS: Record<PurgeReason, string> = {
  consent_withdrawal: 'Consent withdrawn',
  legal_request: 'Legal request',
  uploaded_in_error: 'Uploaded in error',
  other: 'Other (say why)',
  retention_expired: 'Retention window elapsed',
};

export const PURGE_NOTE_MAX = 1000;

export type PurgeInput = { reason: PurgeReason; note?: string | null };

/** A refusal sentence, or null. ⚠️ 'other' with no note is refused: "other" explains nothing in an audit trail. */
export function purgeInputError(input: { reason: string; note?: string | null }): string | null {
  if (!(PURGE_REASONS as readonly string[]).includes(input.reason)) return 'Choose a reason for the purge.';
  const note = (input.note ?? '').trim();
  if (note.length > PURGE_NOTE_MAX) return `Keep the note under ${PURGE_NOTE_MAX} characters.`;
  if (input.reason === 'other' && note.length === 0) return 'Say why, so the record explains itself.';
  return null;
}

export type PurgeTarget = {
  accountId: string;
  originalPath: string;
  thumbPath: string | null;
  previewPath: string | null;
  posterPath: string | null;
  videoPreviewPath: string | null;
};

/**
 * Every object path the asset record names. ⚠️ Each passes assertAssetPath: a
 * path outside the originals/renditions layout is refused, never guessed at.
 */
export function recordedObjectPaths(t: PurgeTarget): string[] {
  return [t.originalPath, t.thumbPath, t.previewPath, t.posterPath, t.videoPreviewPath]
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .map((p) => assertAssetPath(p));
}

/**
 * The two prefixes an asset's objects live under. ⚠️ Deleting BY PREFIX as well
 * as by the recorded paths catches an object the record does not name (a
 * rendition from an earlier attempt, a second original) — a purge that left one
 * behind would not be a purge. The ids are validated by the path builders'
 * own segment rules, and the trailing slash keeps `a1` from matching `a10`.
 */
export function assetPrefixes(accountId: string, assetId: string): { original: string; rendition: string } {
  const seg = /^[A-Za-z0-9_-]{1,128}$/;
  if (!seg.test(accountId) || !seg.test(assetId)) throw new Error('Refusing a purge prefix with an unsafe id.');
  return {
    original: `${ORIGINALS_PREFIX}${accountId}/${assetId}/`,
    rendition: `${RENDITIONS_PREFIX}${accountId}/${assetId}/`,
  };
}

/**
 * The CDN path to invalidate: ONE wildcard per asset covers every rendition.
 * ⚠️ Originals are never served by the CDN (only renditions are), so they need
 * no invalidation.
 */
export function cdnInvalidationPath(accountId: string, assetId: string): string {
  return `/${assetPrefixes(accountId, assetId).rendition}*`;
}

/**
 * ONE wildcard for a whole ACCOUNT's renditions — what a BATCH purge invalidates (one call per account, never one per
 * asset: Google rate-limits cache invalidations per project; see cdnInvalidate.ts). It covers every asset path.
 */
export function cdnAccountInvalidationPath(accountId: string): string {
  // ⚠️ Validated through assetPrefixes with a placeholder asset id, so an unsafe account id is refused the same way.
  const rendition = assetPrefixes(accountId, 'x').rendition; // renditions/<account>/x/
  return `/${rendition.slice(0, rendition.length - 2)}*`; // /renditions/<account>/*
}

/**
 * Does ANY of the invalidated `paths` cover this asset's renditions? A path is a prefix wildcard (`…/*`); it covers the
 * asset when the asset's rendition prefix starts with it. ⚠️ The check "the rest" of a purge makes before it will stamp
 * an asset purged: a purge that was never invalidated, or invalidated for another account, must not complete.
 */
export function invalidationCovers(paths: readonly string[], accountId: string, assetId: string): boolean {
  const rendition = `/${assetPrefixes(accountId, assetId).rendition}`;
  return paths.some((p) => p.endsWith('/*') && rendition.startsWith(p.slice(0, -1)));
}

/**
 * What the confirmation says. ⚠️ Pure and tested: it must name the asset, say
 * it is irreversible, and not claim the bytes are instantly gone everywhere —
 * Google keeps a recovery copy for its bucket soft-delete window, and a signed
 * URL already issued works until its window ends.
 */
export function purgeConfirmation(asset: { title?: string | null; originalFilename: string }): {
  title: string;
  file: string | null;
  body: string[];
} {
  const name = `“${assetDisplayName(asset)}”`;
  return {
    title: `Purge ${name} permanently?`,
    file: originalFileLine(asset),
    body: [
      `This deletes the original and every preview of ${name} from storage and clears it from the CDN. Kyck cannot restore it afterwards.`,
      'It is for a takedown (for example consent withdrawn). It does not remove the photo from anything already published, and a link that was already issued can keep working until its window ends — see the takedown runbook for what to tell the person asking.',
    ],
  };
}
